import React, { useState, useEffect } from 'react';

const HOPPyAIChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState('welcome');
  const [userIntent, setUserIntent] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);

  useEffect(() => {
    const handleOpenChat = () => setIsOpen(true);
    window.addEventListener('openHOPPyChat', handleOpenChat);
    return () => window.removeEventListener('openHOPPyChat', handleOpenChat);
  }, []);

  const intents = {
    'build_on_owned_plot': {
      title: 'Van telkem, építkeznék',
      route: '/epitkezes',
      questions: [
        {
          id: 'location',
          text: 'Melyik városban vagy környéken van a telek?',
          type: 'text'
        },
        {
          id: 'size',
          text: 'Körülbelül mekkora házat szeretnél?',
          type: 'buttons',
          options: ['50–80 m²', '80–120 m²', '120–180 m²', '180+ m²']
        },
        {
          id: 'style',
          text: 'Milyen stílust szeretnél?',
          type: 'buttons',
          options: ['Modern', 'Klasszikus', 'Prémium', 'Befektetésre']
        },
        {
          id: 'tech',
          text: 'Milyen építési megoldás érdekel?',
          type: 'buttons',
          options: ['Moduláris', 'Könnyűszerkezetes', 'Hagyományos', 'Több opció is']
        }
      ]
    },
    'search_plot': {
      title: 'Nincs telkem, keresek',
      route: '/telek',
      questions: [
        {
          id: 'location',
          text: 'Melyik környéken keresel?',
          type: 'text'
        },
        {
          id: 'type',
          text: 'Telket keresel vagy kész ingatlant?',
          type: 'buttons',
          options: ['Telket', 'Kész ingatlant', 'Mindkettőt']
        },
        {
          id: 'budget',
          text: 'Mekkora keretben gondolkodsz?',
          type: 'text'
        },
        {
          id: 'purpose',
          text: 'Saját célra keresed vagy befektetésre?',
          type: 'buttons',
          options: ['Saját célra', 'Befektetésre', 'Mindkettő']
        }
      ]
    },
    'sell_property': {
      title: 'Eladnám az ingatlanom',
      route: '/property-upload',
      questions: [
        {
          id: 'type',
          text: 'Mit adnál el?',
          type: 'buttons',
          options: ['Lakás', 'Ház', 'Telek', 'Egyéb ingatlan']
        },
        {
          id: 'location',
          text: 'Hol található?',
          type: 'text'
        },
        {
          id: 'photos',
          text: 'Van már róla fotód vagy videód?',
          type: 'buttons',
          options: ['Igen', 'Még nincs']
        },
        {
          id: 'ai_help',
          text: 'Szeretnéd, hogy AI segítsen a hirdetés elkészítésében?',
          type: 'buttons',
          options: ['Igen', 'Igen, teljesen automatikusan', 'Inkább csak érdeklődöm']
        }
      ]
    },
    'search_property': {
      title: 'Ingatlant keresek',
      route: '/elado-ingatlanok',
      questions: [
        {
          id: 'location',
          text: 'Hol keresel ingatlant?',
          type: 'text'
        },
        {
          id: 'purpose',
          text: 'Mire keresed?',
          type: 'buttons',
          options: ['Saját otthon', 'Befektetés', 'Kiadásra']
        },
        {
          id: 'size',
          text: 'Mekkora méret érdekel?',
          type: 'text'
        },
        {
          id: 'priority',
          text: 'Mi a legfontosabb számodra?',
          type: 'buttons',
          options: ['Ár', 'Lokáció', 'Állapot', 'Gyors költözés']
        }
      ]
    }
  };

  const addMessage = (text, isBot = true) => {
    setChatHistory(prev => [...prev, { text, isBot, timestamp: Date.now() }]);
  };

  const handleIntentSelect = (intent) => {
    setUserIntent(intent);
    setCurrentStep('questions');
    addMessage(`Szuper! ${intents[intent].title}. Pár gyors kérdés, és már mutatom is a lehetőségeket.`);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setCurrentStep('welcome');
      setUserIntent(null);
      setChatHistory([]);
    }, 300);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-pink-600 hover:to-purple-600 text-white font-black p-4 rounded-full shadow-2xl hover:shadow-purple-500/50 transition-all hover:scale-110 animate-pulse"
      >
        <div className="flex items-center gap-2">
          <span className="text-2xl">🤖</span>
          <span className="hidden md:inline">HOPPy segít</span>
        </div>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-full max-w-md">
      <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] rounded-2xl shadow-2xl border-2 border-purple-500/30 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-3xl">🤖</div>
            <div>
              <h3 className="text-white font-black">HOPPy</h3>
              <p className="text-white/80 text-xs">AI lakhatási asszisztens</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Chat Area */}
        <div className="h-96 overflow-y-auto p-4 space-y-4">
          {currentStep === 'welcome' && (
            <>
              {/* Welcome Message */}
              <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl p-4 border border-purple-500/30">
                <p className="text-white mb-4">
                  <strong>Szia, HOPPy vagyok! 🤖</strong>
                  <br />
                  Segítek megtalálni a legjobb ingatlan vagy építési megoldást.
                  <br />
                  <br />
                  Miben segíthetek?
                </p>
              </div>

              {/* Intent Buttons */}
              <div className="space-y-2">
                <button
                  onClick={() => handleIntentSelect('build_on_owned_plot')}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-emerald-600 hover:to-green-500 text-white font-bold py-3 px-4 rounded-xl transition-all text-left"
                >
                  🏗️ Van telkem, építkeznék
                </button>
                <button
                  onClick={() => handleIntentSelect('search_plot')}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-cyan-600 hover:to-blue-500 text-white font-bold py-3 px-4 rounded-xl transition-all text-left"
                >
                  🗺️ Nincs telkem, keresek
                </button>
                <button
                  onClick={() => handleIntentSelect('sell_property')}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-600 hover:from-orange-600 hover:to-red-500 text-white font-bold py-3 px-4 rounded-xl transition-all text-left"
                >
                  🏠 Eladnám az ingatlanom
                </button>
                <button
                  onClick={() => handleIntentSelect('search_property')}
                  className="w-full bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-amber-600 hover:to-yellow-500 text-white font-bold py-3 px-4 rounded-xl transition-all text-left"
                >
                  🔍 Ingatlant keresek
                </button>
              </div>
            </>
          )}

          {currentStep === 'questions' && userIntent && (
            <>
              {/* Chat History */}
              {chatHistory.map((msg, idx) => (
                <div
                  key={idx}
                  className={`${
                    msg.isBot
                      ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30'
                      : 'bg-gradient-to-br from-white/10 to-white/5 border-white/[0.08] ml-8'
                  } rounded-2xl p-4 border`}
                >
                  <p className="text-white text-sm">{msg.text}</p>
                </div>
              ))}

              {/* Questions would go here - simplified for now */}
              <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-2xl p-4 border border-green-500/30">
                <p className="text-white text-sm mb-3">
                  Rendben! Átirányítalak a megfelelő oldalra, ahol folytathatod.
                </p>
                <button
                  onClick={() => window.location.href = intents[userIntent].route}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-emerald-600 hover:to-green-500 text-white font-black py-3 rounded-xl transition-all"
                >
                  Indulhat! 🚀
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HOPPyAIChat;
